import React, { useState, ChangeEvent, FormEvent } from 'react';
import './App.css';
import { HERO_IMAGE, FLOW_IMAGE, EXAMPLE_IMAGE } from './constants';
import { addToWaitlist } from './services/supabase';
import {
  Container,
  HeroSection,
  SubHeading,
  Section,
  FeatureSection,
  FeatureCard,
  FeatureTitle,
  FeatureDescription,
  HowItWorksSection,
  StepContainer,
  Step,
  StepNumber,
  FormContainer,
  Form,
  FormGroup,
  Label,
  Input,
  Button,
  SmallText,
  FAQSection,
  FAQItem,
  FAQQuestion,
  FAQAnswer,
  Divider,
  Footer,
  ImageContainer,
  HeroImg,
  FlowImg,
  ExampleImg,
  InputGroup,
  OptionalFields,
  OptionalLabel,
  TextArea,
  ErrorMessage
} from './styles';

const App: React.FC = () => {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [interests, setInterests] = useState('');
  const [featurePreferences, setFeaturePreferences] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    
    if (!email.trim()) {
      setError('Email is required');
      return;
    }
    
    setIsSubmitting(true);
    setError('');
    setSuccess(false);
    
    try {
      // Submit to Supabase
      await addToWaitlist({
        email,
        name: name || undefined,
        interests: interests || undefined,
        feature_preferences: featurePreferences || undefined,
        validation_campaign: 'figsync'
      });
      
      // Reset form
      setEmail('');
      setName('');
      setInterests('');
      setFeaturePreferences('');
      setSuccess(true);
    } catch (err) {
      console.error('Waitlist submission error:', err);
      setError(err instanceof Error ? err.message : 'Failed to submit. Please try again later.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <div className="bg-accent"></div>
      <Container>
        <HeroSection>
          <ImageContainer>
              <FlowImg src={FLOW_IMAGE} alt="How FigSync works" />
          </ImageContainer>
          <SubHeading>
            Continuous integration for UI: every change you make in Figma ships clean, typed code to your repo with <strong>FigSync</strong>.
          </SubHeading>
          <p>
            <em>From pixel push to pull request in one click.</em> <strong>Early access is limited—join the waitlist below.</strong>
          </p>
        </HeroSection>

        <Divider />

        <Section>
          <h2>Why FigSync for DesignOps?</h2>
          <FeatureSection>
            <FeatureCard>
              <FeatureTitle>Design‑to‑Code Pipeline</FeatureTitle>
              <FeatureDescription>
                Treat UI the same way you treat backend infra. FigSync watches your Figma file, generates production‑ready React/TypeScript, and opens a pull request for every commit‑level change.
              </FeatureDescription>
            </FeatureCard>
            
            <FeatureCard>
              <FeatureTitle>Bidirectional Source of Truth</FeatureTitle>
              <FeatureDescription>
                Designers iterate in Figma; developers refactor in code. FigSync syncs both directions so design docs and code stay 1:1—no hand‑offs, no drift.
              </FeatureDescription>
            </FeatureCard>
            
            <FeatureCard>
              <FeatureTitle>Git‑Style Guardrails</FeatureTitle>
              <FeatureDescription>
                Layer‑level diffing, branch previews, and commit history bring DevOps safety nets to the design workflow. Experiment freely, merge confidently.
              </FeatureDescription>
            </FeatureCard>
            
            <FeatureCard>
              <FeatureTitle>Works With Your Stack</FeatureTitle>
              <FeatureDescription>
                Compatible with Storybook, component libraries, and your existing CI/CD. Zero vendor lock‑in—just faster releases.
              </FeatureDescription>
            </FeatureCard>
          </FeatureSection>
        </Section>

        <Divider />

        <HowItWorksSection>
          <h2>How It Works</h2>
          <ImageContainer>
            <HeroImg src={HERO_IMAGE} alt="DesignOps Delivered" />
          </ImageContainer>
          <StepContainer>
            <Step>
              <StepNumber>1</StepNumber>
              <h3>Connect</h3>
              <p>your GitHub repo & Figma file.</p>
            </Step>
            
            <Step>
              <StepNumber>2</StepNumber>
              <h3>Map</h3>
              <p>or auto‑detect components once.</p>
            </Step>
            
            <Step>
              <StepNumber>3</StepNumber>
              <h3>Design</h3>
              <p>FigSync opens a PR with generated code for each change.</p>
            </Step>
            
            <Step>
              <StepNumber>4</StepNumber>
              <h3>Review & merge</h3>
              <p>tests pass, code ships, design stays in sync.</p>
            </Step>
          </StepContainer>
        </HowItWorksSection>

        <Divider />

        <Section>
          <h2>Who's It For?</h2>
          <FeatureSection>
            <FeatureCard>
              <FeatureTitle>Design‑system teams</FeatureTitle>
              <FeatureDescription>
                scaling consistency.
              </FeatureDescription>
            </FeatureCard>
            
            <FeatureCard>
              <FeatureTitle>DevOps engineers</FeatureTitle>
              <FeatureDescription>
                extending automation to the front‑end.
              </FeatureDescription>
            </FeatureCard>
            
            <FeatureCard>
              <FeatureTitle>Product squads</FeatureTitle>
              <FeatureDescription>
                that want UI velocity without sacrificing code quality.
              </FeatureDescription>
            </FeatureCard>
          </FeatureSection>
        </Section>

        <Divider />

        <Section id="waitlist">
          <h2>Join the Waitlist</h2>
          <FormContainer>
            <Form onSubmit={handleSubmit}>
              <Label htmlFor="email">Enter your email below to join the FigSync waitlist:</Label>
              <InputGroup>
                <Input 
                  type="email" 
                  id="email" 
                  placeholder="Your email address *"
                  value={email}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
                  required
                  disabled={isSubmitting}
                />
                <Button type="submit" disabled={isSubmitting}>
                  {isSubmitting ? 'Submitting...' : 'Join the waitlist'}
                </Button>
              </InputGroup>
              
              {error && <ErrorMessage>{error}</ErrorMessage>}
              {success && <SmallText style={{ color: '#28a745', marginTop: '10px' }}>Thanks for joining! We'll be in touch soon.</SmallText>}
              
              <OptionalFields>
                <OptionalLabel>Optional information to help us understand your needs:</OptionalLabel>
                
                <FormGroup>
                  <Label htmlFor="name">Your Name:</Label>
                  <Input 
                    type="text" 
                    id="name" 
                    placeholder="Your name (optional)"
                    value={name}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => setName(e.target.value)}
                    disabled={isSubmitting}
                  />
                </FormGroup>
                
                <FormGroup>
                  <Label htmlFor="interests">Why are you interested in FigSync?</Label>
                  <TextArea 
                    id="interests" 
                    placeholder="Tell us about your current design-to-code challenges or what drew you to FigSync (optional)"
                    value={interests}
                    onChange={(e: ChangeEvent<HTMLTextAreaElement>) => setInterests(e.target.value)}
                    rows={3}
                    disabled={isSubmitting}
                  />
                </FormGroup>
                
                <FormGroup>
                  <Label htmlFor="featurePreferences">Which features are most interesting to you?</Label>
                  <TextArea 
                    id="featurePreferences" 
                    placeholder="Let us know which features matter most to your workflow (optional)"
                    value={featurePreferences}
                    onChange={(e: ChangeEvent<HTMLTextAreaElement>) => setFeaturePreferences(e.target.value)}
                    rows={3}
                    disabled={isSubmitting}
                  />
                </FormGroup>
              </OptionalFields>
            </Form>
            <SmallText>We'll email you an invite as soon as slots open.</SmallText>
          </FormContainer>
        </Section>

        <Divider />

        <FAQSection>
          <h2>FAQ</h2>
          
          <FAQItem>
            <FAQQuestion>What is DesignOps?</FAQQuestion>
            <FAQAnswer>
              DesignOps applies DevOps principles—automation, version control, continuous delivery—to the design layer. FigSync gives you a DesignOps pipeline out of the box.
            </FAQAnswer>
          </FAQItem>
          
          <FAQItem>
            <FAQQuestion>Will FigSync overwrite my custom code?</FAQQuestion>
            <FAQAnswer>
              Never. We only touch the blocks linked to your Figma components, preserving logic and state.
            </FAQAnswer>
          </FAQItem>
          
          <FAQItem>
            <FAQQuestion>Which stacks are supported?</FAQQuestion>
            <FAQAnswer>
              Private beta launches with React + TypeScript; Vue and Svelte are next. Tell us what you need on sign‑up.
            </FAQAnswer>
          </FAQItem>
          
          <FAQItem>
            <FAQQuestion>Is my data secure?</FAQQuestion>
            <FAQAnswer>
              All syncing happens over encrypted channels, and we never store your assets beyond processing.
            </FAQAnswer>
          </FAQItem>
        </FAQSection>

        <Divider />

        <Section style={{ textAlign: 'center' }}>
          <h3>Stop treating design and code as silos.</h3>
          <p><strong>Adopt DesignOps with FigSync → Join the waitlist now.</strong></p>
        </Section>

        <Footer>
          © 2025 FigSync. Build once, ship everywhere.
        </Footer>
      </Container>
    </>
  );
}

export default App;
